// ✅ src/pages/VehicleV2/VehicleStatusReport_SeparatePrint.jsx
// FULL UPDATED FILE
//
// ✅ common localStorage / sessionStorage pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ sends inventory headers
// ✅ reads filters from navigate state OR querystring
// ✅ supports old + new param names
// ✅ calls backend: GET /api/vehicleStatusReport/print/separate
// ✅ renders separate print report in A4 landscape
//
// Backend route expected:
//   GET /api/vehicleStatusReport/print/separate

import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/vehicleStatusReport";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDateAny = (v) => {
  if (!v) return "";
  const s = String(v).trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s.replace(/-/g, "/");

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeRow = (r, idx) => ({
  sno: idx + 1,
  VehicleType: r?.VehicleType ?? r?.vehicleMainType ?? r?.vehicleType ?? "",
  fld_vehicle_code: r?.fld_vehicle_code ?? r?.vehicleCode ?? "",
  fld_vehicle_name: r?.fld_vehicle_name ?? r?.vehicleName ?? "",
  fld_vehicle_model: r?.fld_vehicle_model ?? r?.vehicleModel ?? "",
  fld_vehicle_regno: r?.fld_vehicle_regno ?? r?.regNo ?? "",
  fld_EquipmentSLNO: r?.fld_EquipmentSLNO ?? r?.equipmentSerialNo ?? "",
  fld_purchaseddate: fmtDateAny(r?.fld_purchaseddate ?? r?.purchaseDate),
  fld_purchased_amt: fmtNum2(r?.fld_purchased_amt ?? r?.purchaseValue),
  fld_cummulativehr: fmtNum2(r?.fld_cummulativehr ?? r?.cumHr),
  fld_cummulativekm: fmtNum2(r?.fld_cummulativekm ?? r?.cumKm),
  fld_progressentrydate: fmtDateAny(r?.fld_progressentrydate ?? r?.lastUpdate),
  fld_status: r?.fld_status ?? r?.status ?? "",
  fld_vehicle_basistype: r?.fld_vehicle_basistype ?? r?.vehicleBasis ?? "",
  location: r?.location ?? r?.branch ?? "",
});

export default function VehicleStatusReport_SeparatePrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  /* ===========================
     refs / states
  =========================== */
  const didInitRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  /* ===========================
     filters from state or query
  =========================== */
  const filters = useMemo(() => {
    const st = location?.state || {};

    const getVal = (...keys) => {
      for (const key of keys) {
        if (st?.[key] !== undefined && st?.[key] !== null && String(st[key]).trim() !== "") {
          return st[key];
        }
        const qp = searchParams.get(key);
        if (qp !== null && qp !== undefined && String(qp).trim() !== "") {
          return qp;
        }
      }
      return "";
    };

    const vehicleModeRaw = toStr(
      getVal("vehicleMode", "vehMode", "vehtypemode") || "O"
    ).toUpperCase();

    return {
      vehicleMode: vehicleModeRaw === "H" ? "H" : "O",

      // branch print side expects branch name usually
      branch: toStr(getVal("branch") || "Select"),

      regNo: toStr(getVal("regNo", "regno")),
      vehCode: toStr(getVal("vehCode", "vehcode", "vehicleCode")),
      vehName: toStr(getVal("vehName", "vehname", "vehicleName")),

      month: toStr(getVal("month") || "Select"),
      year: toStr(getVal("year") || "Select"),

      vehtype: toStr(getVal("vehtype") || "Select"),
      vehicletype: toStr(getVal("vehicletype", "vehicleMainType") || "Select"),

      vehicleStatus: toStr(getVal("vehicleStatus", "status") || "Select"),
    };
  }, [location?.state, searchParams]);

  /* ===========================
     request param builder
  =========================== */
  const buildParams = useCallback(() => {
    return {
      branchType: session.branchType,
      branchId: session.branchId,
      userId: session.userId,

      vehicleMode: filters.vehicleMode,

      // print backend uses branch as branch name filter
      branch: isSelect(filters.branch) ? "" : toStr(filters.branch),

      regNo: toStr(filters.regNo),
      vehicleCode: toStr(filters.vehCode),
      vehicleName: toStr(filters.vehName),

      month: isSelect(filters.month) ? "" : toStr(filters.month),
      year: isSelect(filters.year) ? "" : toStr(filters.year),

      vehicleMainType: isSelect(filters.vehicletype)
        ? isSelect(filters.vehtype)
          ? ""
          : toStr(filters.vehtype)
        : toStr(filters.vehicletype),

      vehicleStatus: isSelect(filters.vehicleStatus)
        ? ""
        : toStr(filters.vehicleStatus),
    };
  }, [filters, session.branchType, session.branchId, session.userId]);

  /* ===========================
     loader
  =========================== */
  const loadPrintData = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");

      const params = buildParams();

      const res = await axios.get(`${API_BASE}/print/separate`, {
        params,
        headers: commonHeaders,
      });

      const data = res.data || {};
      if (!data?.success) {
        throw new Error(data?.message || "Failed to load print data");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams, commonHeaders]);

  /* ===========================
     effect
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPrintData();
  }, [loadPrintData]);

  /* ===========================
     computed
  =========================== */
  const printRows = useMemo(() => rows.map(normalizeRow), [rows]);
  const totalVehicles = useMemo(() => printRows.length, [printRows]);

  const filterSummary = useMemo(
    () => ({
      branch: isSelect(filters.branch) ? "All" : filters.branch,
      month: isSelect(filters.month) ? "All" : filters.month,
      year: isSelect(filters.year) ? "All" : filters.year,
      vehtype:
        !isSelect(filters.vehicletype)
          ? filters.vehicletype
          : !isSelect(filters.vehtype)
          ? filters.vehtype
          : "All",
      regNo: filters.regNo || "All",
      vehCode: filters.vehCode || "All",
      vehName: filters.vehName || "All",
      status: isSelect(filters.vehicleStatus) ? "All" : filters.vehicleStatus,
      mode: filters.vehicleMode === "H" ? "Hire" : "Own",
    }),
    [filters]
  );

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @page { size: A4 landscape; margin: 0.3in; }
        @media print {
          body { margin: 0; padding: 0; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; width: 100%; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
            type="button"
          >
            ← Back
          </button>

          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            type="button"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">
            Loading print data...
          </div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">
            {err}
          </div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Vehicle Status Report</h1>
              <div className="text-xs">INFRA SCREEN : B-48/2</div>
              <div className="text-xs">
                Mode: {filterSummary.mode} | Branch: {filterSummary.branch} | Month/Year:{" "}
                {filterSummary.month} / {filterSummary.year}
              </div>
            </div>
          </div>
        </div>

        {/* Filters line */}
        <div className="border border-black p-2 text-[11px] mb-2">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <b>Vehicle Main Type:</b> {filterSummary.vehtype}
            </div>
            <div>
              <b>Reg No:</b> {filterSummary.regNo}
            </div>
            <div>
              <b>Vehicle Code:</b> {filterSummary.vehCode}
            </div>
            <div>
              <b>Vehicle Name:</b> {filterSummary.vehName}
            </div>
            <div>
              <b>Status:</b> {filterSummary.status}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th style={{ minWidth: 35 }} className="font-bold text-center">S.No</th>
                <th style={{ minWidth: 120 }} className="font-bold text-center">Vehicle Main Type</th>
                <th style={{ minWidth: 90 }} className="font-bold text-center">Vehicle Code</th>
                <th style={{ minWidth: 120 }} className="font-bold text-center">Vehicle Name</th>
                <th style={{ minWidth: 90 }} className="font-bold text-center">Vehicle Model</th>
                <th style={{ minWidth: 120 }} className="font-bold text-center">Vehicle Registration No</th>
                <th style={{ minWidth: 120 }} className="font-bold text-center">Equipment Serial No</th>
                <th style={{ minWidth: 90 }} className="font-bold text-center">Purchase Date</th>
                <th style={{ minWidth: 90 }} className="font-bold text-center">Purchase Value</th>
                <th style={{ minWidth: 110 }} className="font-bold text-center">Current Cummulative Hr</th>
                <th style={{ minWidth: 110 }} className="font-bold text-center">Current Cummulative KM</th>
                <th style={{ minWidth: 90 }} className="font-bold text-center">Last Update Date</th>
                <th style={{ minWidth: 100 }} className="font-bold text-center">Current Status</th>
                <th style={{ minWidth: 110 }} className="font-bold text-center">Vehicle Basis</th>
                <th style={{ minWidth: 110 }} className="font-bold text-center">Location</th>
              </tr>
            </thead>

            <tbody>
              {!printRows.length ? (
                <tr>
                  <td colSpan={15} className="text-center py-3">
                    No data to print.
                  </td>
                </tr>
              ) : (
                printRows.map((r) => (
                  <tr key={`${r.sno}-${r.fld_vehicle_code}-${r.fld_vehicle_regno}`}>
                    <td className="text-center">{r.sno}</td>
                    <td>{r.VehicleType}</td>
                    <td className="text-center">{r.fld_vehicle_code}</td>
                    <td>{r.fld_vehicle_name}</td>
                    <td className="text-center">{r.fld_vehicle_model}</td>
                    <td className="text-center">{r.fld_vehicle_regno}</td>
                    <td className="text-center">{r.fld_EquipmentSLNO}</td>
                    <td className="text-center">{r.fld_purchaseddate}</td>
                    <td className="text-right">{r.fld_purchased_amt}</td>
                    <td className="text-right">{r.fld_cummulativehr}</td>
                    <td className="text-right">{r.fld_cummulativekm}</td>
                    <td className="text-center">{r.fld_progressentrydate}</td>
                    <td className="text-center">{r.fld_status}</td>
                    <td className="text-center">{r.fld_vehicle_basistype}</td>
                    <td className="text-center">{r.location}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-2 flex items-center justify-between">
          <div>
            Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
          </div>
          <div>
            <b>Total Number of Vehicles:</b> {totalVehicles}
          </div>
        </div>

        {/* bottom print */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            type="button"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}